<template>
  <div>
    <q-dialog v-model="isDialogueVisible" @hide="resetData">
      <q-card class="new-rule-card">
        <q-form @submit.prevent="onSubmit" class="q-gutter-md">
          <q-card-section class="title">
            <div v-if="duplicateCurrent">Create new rule from {{ rule.id }}</div>
            <div v-else>Create new rule</div>
          </q-card-section>

          <q-scroll-area class="scroll-area">
            <q-card-section class="content-wrapper">
              <q-input
                :error="v$.name.$error && !v$.name.$pending"
                :error-message="
                  v$.name.required.$invalid
                    ? v$.name.required.$message
                    : v$.name.minLength.$invalid
                      ? v$.name.minLength.$message
                      : v$.name.maxLength.$invalid
                        ? v$.name.maxLength.$message
                        : v$.name.isUnique.$invalid
                          ? v$.name.isUnique.$message
                          : undefined
                "
                :loading="v$.name.isUnique.$pending"
                autofocus
                bottom-slots
                class="q-mt-sm q-mb-md"
                debounce="500"
                filled
                label="Name"
                placeholder="e.g.: fraud_high_risk_markets"
                type="text"
                v-model="state.name"
              >
                <template #hint v-if="state.name && state.name !== normalizedName">
                  Will be normalized to <b>{{ normalizedName }}</b>
                </template>
              </q-input>

              <q-select
                :error-message="v$.apiSchema.required.$message"
                :error="v$.apiSchema.$error"
                :options="apiSchemas"
                bottom-slots
                filled
                label="API Schema"
                v-model="state.apiSchema"
              >
                <template #hint v-if="state.apiSchema">
                  <a @click.capture.stop class="hint-link" :href="apiLink" target="_blank">
                    More about {{ state.apiSchema }} schema
                    <q-icon name="fas fa-arrow-right" />
                  </a>
                </template>
              </q-select>

              <q-input
                :error-message="v$.issue.required.$message"
                :error="v$.issue.$error"
                @keyup.enter="prompt = false"
                class="q-mt-md q-mb-md"
                filled
                label="Issue reference"
                placeholder="e.g.: Q2200946 or IRIS-4279"
                type="text"
                v-model="state.issue"
              >
                <template #hint v-if="state.issue && state.issue !== normalizedIssue">
                  Will be normalized to <b>{{ normalizedIssue }}</b>
                </template>
              </q-input>

              <q-input
                :error-message="v$.description.required.$message"
                :error="v$.description.$error"
                @keyup.enter="prompt = false"
                dense
                filled
                label="Description"
                placeholder="A short explanation of the purpose of this rule"
                size="sm"
                type="textarea"
                v-model="state.description"
              />

              <q-field borderless class="q-mt-md q-mr-md" v-if="isIPAEnabled">
                <q-item-section avatar top>
                  <q-checkbox v-model="useRuleBuilder" />
                </q-item-section>
                <q-item-section>
                  <q-item-label> Try experimental graphical Rule Builder (BETA) </q-item-label>
                  <q-item-label caption>
                    Use the new graphical Rule Builder to create your rule.<br />
                    Further editing will still use the conventional Rule Builder.<br />
                    Please give us feedback on
                    <!-- eslint-disable-next-line -->
                    <a href="https://klarna.slack.com/archives/CRDPUH9CN" target="_blank">#iris</a>.
                  </q-item-label>
                </q-item-section>
              </q-field>
            </q-card-section>
          </q-scroll-area>

          <q-separator class="separator" />
          <q-card-actions align="right" class="text-secondary card-actions">
            <q-btn class="btn-cancel" flat label="Cancel" v-close-popup />

            <q-btn
              v-if="duplicateCurrent"
              :disable="v$.$invalid"
              :loading="isLoading"
              class="btn-add"
              color="secondary"
              label="Duplicate"
              type="submit"
            />

            <q-btn
              v-else
              :disable="v$.$invalid"
              :loading="isLoading"
              class="btn-add"
              color="secondary"
              label="Create"
              type="submit"
            />
          </q-card-actions>
        </q-form>
      </q-card>
    </q-dialog>
    <rule-builder-dialog ref="ruleBuilderDialog" @built="createRuleFromContent" />
  </div>
</template>

<script lang="ts">
import RuleBuilderDialog from '@/components/common/RuleBuilderDialog.vue'
import { GIT_RELEASE_BRANCH } from '@/config'
import { isProduction } from '@/environment'
import { paths } from '@/router'
import { apiSchemas } from '@/utils/constants'
import { normalizeIssueRef } from '@/utils/issues'
import notifications from '@/utils/notifications'
import { getApiLink, normalizeToValidId, useDirectStore } from '@/utils/utils'
import type { ApiSchema, Rule } from '@klarna/cbrms-policy-rules-manager-client-ts'
import { useVuelidate } from '@vuelidate/core'
import { helpers, maxLength, minLength, required } from '@vuelidate/validators'
import { isAxiosError } from 'axios'
import { computed, defineComponent, reactive, ref } from 'vue'
import { useRouter } from 'vue-router/composables'

interface Props {
  visible: boolean
  duplicateCurrent: boolean
  rule: Rule
}

export default defineComponent<Props>({
  components: { RuleBuilderDialog },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    rule: {
      type: Object,
      default: undefined,
    },
    duplicateCurrent: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, ctx) {
    const { state, dispatch } = useDirectStore()
    const router = useRouter()

    const ruleBuilderDialog = ref<(typeof RuleBuilderDialog)['methods'] | null>(null)
    const useRuleBuilder = ref(false)
    const isLoading = computed(() => state.rule.isLoading)

    const isDialogueVisible = computed({
      get() {
        return props.visible
      },
      set(val) {
        if (!val) {
          ctx.emit('close')
        }
      },
    })

    async function isRuleNameUnique(value: string) {
      if (!value) {
        return true
      }

      if (v$.value.name.$invalid) {
        return true
      }

      try {
        await dispatch.rule.GET_RULE({ ruleId: normalizedName.value, branch: GIT_RELEASE_BRANCH })
        // `GET /rules/{ruleId}/branches/release` succeeded (HTTP 200)
        // Which means that the specified rule name is already in used
        return false
      } catch (err) {
        // `GET /rules/{ruleId}/branches/release` returned a non-200 response
        // If we got a 404, it means the rule name is not taken and the validator should return `true`
        return isAxiosError(err) && err.response?.status === 404
      }
    }

    const data = reactive({
      apiSchema: undefined as ApiSchema | undefined,
      description: '',
      issue: '',
      labels: [],
      name: '',
    })

    const rules = computed(() => ({
      apiSchema: { required },
      description: { required },
      issue: { required },
      name: {
        isUnique: helpers.withMessage(
          ({ $model }) =>
            `Rule with that name already exists${
              $model !== normalizedName.value ? ` (as ${normalizedName.value})` : ''
            }`,
          helpers.withAsync(isRuleNameUnique)
        ),
        maxLength: maxLength(60),
        minLength: minLength(2),
        required,
      },
    }))

    const normalizedIssue = computed(() => normalizeIssueRef(data.issue ?? ''))
    const normalizedName = computed(() => normalizeToValidId(data.name ?? ''))

    const v$ = useVuelidate(rules, data, { $autoDirty: true, $stopPropagation: true })

    function resetData() {
      data.apiSchema = undefined
      data.description = ''
      data.issue = ''
      data.labels = []
      data.name = ''
      v$.value.$reset()
    }

    async function createRuleFromContent(initialContent: string) {
      ruleBuilderDialog.value?.close()
      await createRule(initialContent)
    }

    async function onSubmit() {
      if (useRuleBuilder.value) {
        ruleBuilderDialog.value?.open()
      } else {
        await createRule()
      }
    }

    async function createRule(initialContent?: string) {
      if (!v$.value.$invalid) {
        const labels = props.duplicateCurrent ? props.rule.labels : []
        const content = props.duplicateCurrent ? props.rule.content : initialContent
        const unittests = props.duplicateCurrent ? props.rule.unittests : []
        const contentInputs = props.duplicateCurrent ? props.rule.content_inputs : []
        const contentHelpers = props.duplicateCurrent ? props.rule.content_helpers : []
        const formatVersion = props.duplicateCurrent ? props.rule.format_version : undefined

        const payload = {
          api: data.apiSchema as ApiSchema,
          content,
          content_helpers: contentHelpers,
          content_inputs: contentInputs,
          description: data.description,
          format_version: formatVersion,
          id: normalizedName.value,
          jira_issue: normalizedIssue.value,
          labels,
          unittests,
        }
        await dispatch.rule.CREATE_RULE(payload)

        if (props.duplicateCurrent) {
          dispatch.SHOW_NOTIFICATION({
            message: notifications.successfullyDuplicated(props.rule.id, normalizedName.value),
          })
        }

        router.push(
          paths.ruleBuilder.replace(':rule_id', normalizedName.value).replace(':branch?', state.auth.user!.username)
        )
      }
    }

    return {
      apiLink: computed(() => getApiLink(data.apiSchema)),
      apiSchemas,
      createRuleFromContent,
      isDialogueVisible,
      isIPAEnabled: !isProduction,
      isLoading,
      normalizedName,
      normalizedIssue,
      onSubmit,
      resetData,
      ruleBuilderDialog,
      state: data,
      useRuleBuilder,
      v$,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@/styles/quasar.variables.scss';

.new-rule-card {
  flex: 1;
  min-width: 350px;
  position: relative;
  overflow: hidden;
}

.title {
  width: 100%;
  padding: 0.5em 1em;
  font-size: 15px;
  background-color: $info;
  color: white;
  position: sticky;
  top: 0;
  overflow: hidden;
  font-weight: 600;
  text-overflow: ellipsis;
}

.content-wrapper {
  padding: 0.1em 1em;
  flex: 1;
}

.scroll-area {
  height: 70vh;
}

.card-actions {
  position: sticky;
  bottom: 0;
  padding: 0 1em 1em;
}

.separator {
  margin: 1vh 0;
}

.dialogue-head {
  display: flex;
  justify-content: space-between;
}

.hint-link {
  color: $info;
  text-decoration: none;
  font-weight: 600;
  &:visited,
  &:active {
    color: $info;
    text-decoration: none;
  }
}
</style>
